import { BadgeCheck, FileText, Globe2, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useParams, useSearchParams } from 'react-router';
import { api, isApiError, type SubjectDetail, type SubjectVerification, type SubjectVersion } from '../../api';
import type { SubjectProfile } from '../../api/areas/accounts';
import { Badge } from '../../components/Badge';
import { DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { RevealIdentifier } from '../../components/RevealIdentifier';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { addressLine } from '../../lib/address';
import { formatDate, formatDateTime } from '../../lib/format';
import { isCode, useLabels } from '../../lib/labels';
import { useApi } from '../../lib/useApi';
import { useAccess } from '../../lib/access';
import { ContactRef, CustomerRef, DomainRef, RegistryRef, RoleBadge, useVocab } from '../domains/refs';
import { DOSSIER, humanize, LIFECYCLE, StatusBadge } from '../domains/status';
import { SubjectFormDialog } from './SubjectForm';
import { ReadinessPanel } from './ReadinessPanel';
import { LinkedWork } from '../work/LinkedWork';
import { NavTabs } from '../../components/NavTabs';
import { SubjectDeclarations } from '../declarations/SubjectDeclarations';
import { SubjectDomainSync } from './SubjectDomainSync';
import { Callout } from '../../components/blocks';
import { FactRows, Timeline, TwoColumn } from '../../components/flow';
import { Representative } from '../domains/tabs/SubjectContactsTab';

type Lookup = (key: string, options: { defaultValue: string }) => string;

type SubjectDomain = NonNullable<SubjectDetail['domains']>[number];
type SubjectDossier = NonNullable<SubjectDetail['dossiers']>[number];
type SubjectContact = NonNullable<SubjectDetail['contacts']>[number];

/**
 * Translated label for an enum code in `domains:subject.<group>`, falling back to the humanised
 * code. Free text (a change reason an operator typed) is shown exactly as written.
 */
function useEnumLabel() {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  return (group: 'statuses' | 'types' | 'verificationStatuses' | 'identifierTypes' | 'methods' | 'changeReasons' | 'genders' | 'relationships' | 'addressTypes', value: string) =>
    isCode(value) ? tr(`subject.${group}.${value.toUpperCase()}`, { defaultValue: humanize(value) }) : value;
}

const currentVersionOf = (s: SubjectDetail): SubjectVersion | undefined => {
  const versions = [...(s.versions ?? [])].sort((a, b) => b.version_number - a.version_number);
  return versions.find((v) => v.id === s.current_version_id) ?? versions[0];
};

type ProfileIdentifier = SubjectProfile['identifiers'][number];

/** Stored attribute values as text (nested objects as compact JSON). */
const attributeText = (v: unknown): string => (v === null || v === undefined ? '—' : typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : JSON.stringify(v));

/**
 * Everything stored for the current version (QA 2026-09-28 #2): person / organisation data,
 * contact points, the address with province and ward names, other stored attributes and
 * relationships. The birth date is only "on file" until an audited reveal.
 */
function ProfileSection({ subjectId, p }: { subjectId: string; p: SubjectProfile }) {
  const { t } = useTranslation('domains');
  const label = useEnumLabel();
  const person = p.subject_type === 'INDIVIDUAL';
  const dash = <span className="text-muted-foreground">—</span>;
  const text = (v: string | null | undefined) => (v && v.trim() ? v : dash);
  const vn = p.vn_address;
  const addresses = p.addresses?.length ? p.addresses : p.address ? [{ address_type: 'LEGAL', ...p.address }] : [];
  const others = Object.entries(p.other_attributes ?? {});
  const rows: [string, ReactNode][] = [];
  if (person) {
    rows.push([
      t('subject.profile.birthDate'),
      p.has_birth_date ? (
        <RevealIdentifier
          key="dob"
          identifier={{ id: subjectId, type: 'BIRTH_DATE', country: null, hint: '••/••/••••' }}
          revealer={(reason, opts) => api.revealSubjectBirthDate(subjectId, reason, opts)}
          formatValue={(v) => formatDate(v)}
        />
      ) : (
        <span key="dob" className="text-muted-foreground">{t('subject.profile.birthDateMissing')}</span>
      ),
    ]);
    rows.push([t('subject.profile.gender'), p.gender ? <span key="g" title={p.gender}>{label('genders', p.gender)}</span> : dash]);
  } else {
    rows.push([t('subject.profile.tradeName'), text(p.trade_name)]);
  }
  rows.push([t('subject.profile.position'), text(p.position)]);
  rows.push([t('subject.nationality'), text(p.nationality)]);
  rows.push([t('subject.profile.email'), p.email ? <CopyText key="e" value={p.email} /> : dash]);
  rows.push([t('subject.profile.phone'), text(p.phone)]);
  rows.push([t('subject.profile.fax'), text(p.fax)]);
  if (vn) {
    rows.push([t('subject.profile.street'), text(vn.address)]);
    rows.push([t('subject.profile.ward'), <span key="w" title={vn.ward_id != null ? String(vn.ward_id) : undefined}>{text(vn.ward_name)}</span>]);
    rows.push([t('subject.profile.province'), <span key="pr" title={String(vn.province_id)}>{text(vn.province_name)}</span>]);
    rows.push([t('subject.profile.fullAddress'), text(vn.full_address)]);
    const vnRow = addresses.find((a) => a.country_code === 'VN');
    rows.push([t('subject.profile.postalCode'), text(vnRow?.postal_code ?? null) ?? <span key="pc" className="text-muted-foreground">—</span>]);
  }
  for (const [i, a] of addresses.entries()) {
    if (vn && i === 0 && a.country_code === 'VN') continue; // the same address, shown by place names above
    rows.push([
      label('addressTypes', a.address_type),
      addressLine(a),
    ]);
  }
  if (p.change_reason) rows.push([t('subject.profile.versionReason'), <span key="r">{label('changeReasons', p.change_reason)}</span>]);
  return (
    <Section title={t('subject.profile.title')} description={t('subject.profile.description')}>
      <KeyValue columns={2} rows={rows} />
      {others.length ? (
        <div className="mt-4">
          <h3 className="mb-2 text-sm font-medium">{t('subject.profile.otherAttributes')}</h3>
          <KeyValue
            columns={2}
            rows={others.map(
              ([k, v]) =>
                [
                  // A stored attribute by its name, never its key (an unknown one in words).
                  (t as unknown as (key: string, o: { defaultValue: string }) => string)(`subject.profile.attribute.${k}`, { defaultValue: k.replace(/_/g, ' ') }),
                  <span key={k} className="font-mono text-xs break-all">
                    {attributeText(v)}
                  </span>,
                ] as const,
            )}
          />
        </div>
      ) : null}
      {p.relationships?.length ? (
        <div className="mt-4">
          <h3 className="mb-2 text-sm font-medium">{t('subject.profile.relationships')}</h3>
          <ul className="grid gap-1 text-sm">
            {p.relationships.map((r) => (
              <li key={`${r.type}-${r.related_subject_id}`}>
                <span title={r.type}>{label('relationships', r.type)}</span>:{' '}
                <EntityRef label={r.related_name ?? r.related_subject_id} to={`/subjects/${encodeURIComponent(r.related_subject_id)}`} id={r.related_subject_id} />{' '}
                <span className="text-muted-foreground">{t('subject.profile.since', { date: formatDate(r.valid_from) })}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Section>
  );
}

/**
 * What the core validator would refuse in the current data before a domain can be registered —
 * the same for every TLD: the gaps against the rules every domain follows once, then what a
 * registry or registrar needs beyond them; the same check as quote and order.
 */
function RegistryReadiness({ subjectId }: { subjectId: string }) {
  const { t } = useTranslation('domains');
  return (
    <Section title={t('subject.readiness.title')} description={t('subject.readiness.description')}>
      <ReadinessPanel subjectId={subjectId} />
    </Section>
  );
}

/** "CCCD (VN)" and the identifier's issue data: "ngày cấp 01/06/2021 · hết hạn …". */
function identifierDetails(t: (k: string, o?: Record<string, unknown>) => string, i: ProfileIdentifier | undefined): string | null {
  if (!i) return null;
  const parts = [
    i.issued_at ? t('subject.profile.issuedAt', { date: formatDate(i.issued_at) }) : null,
    i.expires_at ? t('subject.profile.expiresAt', { date: formatDate(i.expires_at) }) : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** A loaded subject: identity (masked), verifications, domains, dossiers, contacts and versions. */
function SubjectBody({ s, extra }: { s: SubjectDetail; extra?: ReactNode }) {
  const { t } = useTranslation('domains');
  const { t: tp } = useTranslation('domainPage');
  const label = useEnumLabel();
  const vocab = useVocab();
  const L = useLabels();
  const versions = [...(s.versions ?? [])].sort((a, b) => b.version_number - a.version_number);
  const current = currentVersionOf(s);
  const versionNumber = (id: string) => versions.find((v) => v.id === id)?.version_number;

  const verificationColumns: Column<SubjectVerification>[] = [
    { key: 'method', header: t('subject.method'), cell: (v) => <span title={v.method}>{label('methods', v.method)}</span> },
    {
      key: 'status',
      header: t('subject.status'),
      cell: (v) => (
        <Badge tone={v.status === 'VERIFIED' ? 'ok' : v.status === 'EXPIRED' || v.status === 'FAILED' ? 'serious' : 'pending'} title={v.status}>
          {label('verificationStatuses', v.status)}
        </Badge>
      ),
    },
    {
      key: 'version',
      header: t('subject.versionCol'),
      cell: (v) => {
        const n = versionNumber(v.subject_version_id);
        return n ? t('common.version', { n }) : '—';
      },
    },
    { key: 'verified', header: t('subject.verified'), className: 'whitespace-nowrap', cell: (v) => formatDateTime(v.verified_at) },
    { key: 'expires', header: t('subject.expires'), className: 'whitespace-nowrap', cell: (v) => formatDate(v.expires_at) },
    { key: 'request', header: t('subject.requestId'), cell: (v) => (v.provider_reference ? <CopyText value={v.provider_reference} /> : '—') },
    {
      key: 'policy',
      header: t('subject.policy'),
      className: 'min-w-48 whitespace-normal',
      cell: (v) => <span className="text-sm">{L.policy(v.policy_key, v.policy_version)}</span>,
    },
  ];

  const domainColumns: Column<SubjectDomain>[] = [
    { key: 'domain', header: t('subject.domain'), cell: (d) => <DomainRef ascii={d.ascii_name} unicode={d.unicode_name} id={d.id} /> },
    { key: 'lifecycle', header: t('subject.lifecycle'), cell: (d) => <StatusBadge map={LIFECYCLE} value={d.lifecycle_state} /> },
    { key: 'dossier', header: t('subject.dossierState'), cell: (d) => <StatusBadge map={DOSSIER} value={d.dossier_state} /> },
    { key: 'expiry', header: t('subject.expiry'), className: 'whitespace-nowrap', cell: (d) => formatDate(d.registry_expires_at) },
    { key: 'customer', header: t('subject.customer'), cell: (d) => <CustomerRef id={d.customer_id} name={d.customer_name} /> },
  ];

  const dossierColumns: Column<SubjectDossier>[] = [
    {
      key: 'dossier',
      header: t('dossier.title'),
      cell: (x) => (
        <EntityRef
          label={x.unicode_name}
          to={`/dossiers/${encodeURIComponent(x.id)}`}
          id={x.id}
          sub={`${t('common.version', { n: x.dossier_version })} · ${vocab('dossierType', x.dossier_type)}`}
        />
      ),
    },
    { key: 'status', header: t('subject.dossierStatus'), cell: (x) => <StatusBadge map={DOSSIER} value={x.status} /> },
    { key: 'sv', header: t('subject.subjectVersion'), cell: (x) => (x.subject_version_number ? t('common.version', { n: x.subject_version_number }) : '—') },
    { key: 'completed', header: t('subject.completed'), className: 'whitespace-nowrap', cell: (x) => formatDateTime(x.completed_at) },
  ];

  const contactColumns: Column<SubjectContact>[] = [
    { key: 'contact', header: t('subject.contact'), cell: (c) => <ContactRef registry={c.registry_code} handle={c.handle} /> },
    { key: 'registry', header: t('subject.registry'), cell: (c) => <RegistryRef code={c.registry_code} name={c.registry_name} /> },
    {
      key: 'roles',
      header: t('subject.roles'),
      cell: (c) =>
        c.roles.length ? (
          <span className="flex flex-wrap gap-1">
            {c.roles.map((r) => (
              <RoleBadge key={r} role={r} />
            ))}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t('contacts.noRoles')}</span>
        ),
    },
    { key: 'domains', header: t('subject.contactDomains'), numeric: true, cell: (c) => c.domain_count },
  ];

  return (
    <div className="grid grid-cols-1 gap-5">
      <SubjectHero s={s} />
      <TwoColumn
        main={
          <>
            {s.profile ? <ProfileSection subjectId={s.id} p={s.profile} /> : null}
            {s.subject_type !== 'INDIVIDUAL' ? <Representative s={s} /> : null}
            <RegistryReadiness subjectId={s.id} />
            {extra}
            {s.domains ? (
              <Section flush title={t('subject.domains')} description={t('subject.domainsDescription')}>
                <DataTable columns={domainColumns} rows={s.domains} rowKey={(d) => d.id} empty={<EmptyState title={t('subject.noDomains')} icon={Globe2} />} />
              </Section>
            ) : null}
            {s.dossiers ? (
              <Section flush title={t('subject.dossiers')}>
                <DataTable columns={dossierColumns} rows={s.dossiers} rowKey={(x) => x.id} empty={<EmptyState title={t('subject.noDossiers')} icon={FileText} />} />
              </Section>
            ) : null}
            {s.contacts ? (
              <Section flush title={t('subject.contacts')} description={t('subject.contactsDescription')}>
                <DataTable
                  columns={contactColumns}
                  rows={s.contacts}
                  rowKey={(c) => `${c.registry_code} ${c.handle}`}
                  empty={<EmptyState title={t('subject.noContacts')} icon={Users} />}
                />
              </Section>
            ) : null}
            <Section flush title={t('subject.verifications')}>
              <DataTable
                columns={verificationColumns}
                rows={s.verifications}
                rowKey={(v) => v.id}
                empty={
                  <EmptyState title={t('subject.noVerifications')} icon={BadgeCheck}>
                    {t('subject.noVerificationsBody')}
                  </EmptyState>
                }
              />
            </Section>
          </>
        }
        side={
          <>
            <VerificationCard s={s} />
            <Section title={t('subject.identifiers')} description={t('subject.identifiersDescription')}>
              {current && current.identifiers.length ? (
                <KeyValue
                  rows={current.identifiers.map((i) => {
                    const details = identifierDetails(t as unknown as (k: string, o?: Record<string, unknown>) => string, s.profile?.identifiers.find((x) => x.id === i.id));
                    return [
                      `${label('identifierTypes', i.type)}${i.country ? ` (${i.country})` : ''}`,
                      <div key={i.id} className="grid gap-1">
                        <RevealIdentifier identifier={i} />
                        {details ? <span className="text-xs text-muted-foreground">{details}</span> : null}
                      </div>,
                    ] as const;
                  })}
                />
              ) : (
                <p className="text-sm text-muted-foreground">{t('subject.noIdentifiers')}</p>
              )}
            </Section>
            <Section title={t('subject.versions')} description={tp('subjectPage.versionsHint')}>
              <Timeline
                items={versions.map((v) => ({
                  id: v.id,
                  title: `v${v.version_number} · ${v.legal_name}`,
                  meta: `${formatDateTime(v.created_at)}${v.change_reason ? ` · ${label('changeReasons', v.change_reason)}` : ''}`,
                  tone: v.id === s.current_version_id ? 'pending' : 'neutral',
                }))}
              />
            </Section>
            <Section title={t('subject.managingCustomer')}>
              <FactRows
                rows={[
                  [t('subject.managingCustomer'), s.managing_customer_id || !s.hostbill_client_id ? <CustomerRef key="c" id={s.managing_customer_id} name={s.managing_customer_name} /> : `HostBill #${s.hostbill_client_id}`],
                  [t('subject.id'), <CopyText key="id" value={s.id} />],
                ]}
              />
            </Section>
            <Callout title={tp('subjectPage.controlledTitle')}>{tp('subjectPage.controlledBody')}</Callout>
          </>
        }
      />
    </div>
  );
}

/** The subject at a glance (v2.1 "Chi tiết chủ thể"): initials, name, type · country, verification, domains, version. */
function SubjectHero({ s }: { s: SubjectDetail }) {
  const { t } = useTranslation('domains');
  const { t: tp } = useTranslation('domainPage');
  const label = useEnumLabel();
  const current = currentVersionOf(s);
  const name = current?.legal_name ?? t('subject.fallbackTitle');
  const initials = name
    .split(/\s+/)
    .filter((w) => w && w[0] === w[0]?.toUpperCase())
    .slice(-2)
    .map((w) => w[0])
    .join('');
  // Owner decision 2026-10-01: an individual verified only by a signed declaration is not
  // verified for a new operation (eKYC only).
  const verified = s.verifications.some((v) => v.status === 'VERIFIED' && v.subject_version_id === s.current_version_id && !signedOnlyIndividual(s, v.method));
  return (
    <section className="flex flex-wrap items-center gap-4 rounded-xl border bg-card p-5" data-subject-hero>
      <span className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-accent text-lg font-bold text-accent-foreground">{initials || '·'}</span>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="text-lg font-semibold break-words">{name}</div>
        <div className="text-xs text-muted-foreground">
          {label('types', s.subject_type)} · {current?.country_code ?? '—'} · <span className="font-mono">{s.id.slice(0, 8)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={s.status === 'ACTIVE' ? 'ok' : 'neutral'}>{label('statuses', s.status)}</Badge>
          <Badge tone={verified ? 'ok' : 'warn'}>{verified ? tp('subjectPage.verified') : tp('subjectPage.notVerified')}</Badge>
          <Badge tone="info" icon={false}>
            {tp('subjectPage.domains', { count: s.domains?.length ?? 0 })}
          </Badge>
          {current ? (
            <Badge tone="neutral" icon={false}>
              {tp('subjectPage.version', { n: current.version_number })}
            </Badge>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/** An individual's signed declaration no longer verifies it for a new operation (owner decision 2026-10-01: eKYC only). */
function signedOnlyIndividual(s: SubjectDetail, method: string): boolean {
  return s.subject_type === 'INDIVIDUAL' && method !== 'EKYC';
}

/** "Xác minh danh tính": the current version's latest verification (eKYC or digital signature). */
function VerificationCard({ s }: { s: SubjectDetail }) {
  const { t } = useTranslation('domains');
  const { t: tp } = useTranslation('domainPage');
  const label = useEnumLabel();
  const L = useLabels();
  const v = [...s.verifications].filter((x) => x.subject_version_id === s.current_version_id).sort((a, b) => ((a.verified_at ?? '') < (b.verified_at ?? '') ? 1 : -1))[0];
  return (
    <Section title={tp('subjectPage.verification')}>
      {v ? (
        <FactRows
          rows={[
            [
              t('subject.status'),
              v.status === 'VERIFIED' && signedOnlyIndividual(s, v.method) ? (
                <span key="s" className="grid gap-1">
                  <Badge tone="warn">{tp('subjectPage.needsEkyc')}</Badge>
                  <span className="text-xs text-muted-foreground">{tp('subjectPage.needsEkycNote')}</span>
                </span>
              ) : (
                <Badge key="s" tone={v.status === 'VERIFIED' ? 'ok' : v.status === 'EXPIRED' || v.status === 'FAILED' ? 'serious' : 'pending'}>{label('verificationStatuses', v.status)}</Badge>
              ),
            ],
            [t('subject.method'), label('methods', v.method)],
            [t('subject.verified'), formatDateTime(v.verified_at)],
            [t('subject.expires'), formatDate(v.expires_at)],
            [t('subject.requestId'), v.provider_reference ? <CopyText key="r" value={v.provider_reference} /> : '—'],
            [t('subject.policy'), L.policy(v.policy_key, v.policy_version)],
          ]}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{tp('subjectPage.noVerification')}</p>
      )}
      {v && v.status === 'PENDING' && s.subject_type !== 'INDIVIDUAL' ? <RegistryLookupAction verificationId={v.id} /> : null}
    </Section>
  );
}

/**
 * An organisation's pending verification: its tax code looked up in the business registry, the
 * answer kept as evidence of the verification (OT&E finding F16). Staff still decide nothing by
 * hand: a subject is verified by eKYC or a digitally signed declaration.
 */
function RegistryLookupAction({ verificationId }: { verificationId: string }) {
  const { t } = useTranslation('accounts');
  const access = useAccess();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!access.has(['business_registry.lookup'])) return null;
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.lookupVerificationRegistry(verificationId);
      setDone(
        t('taxLookup.verificationDone', {
          found: r.found ? t('taxLookup.verificationFound') : t('taxLookup.verificationNotFound'),
          name: r.name_matches === true ? t('taxLookup.verificationNameMatches') : r.name_matches === false ? t('taxLookup.verificationNameDiffers') : t('taxLookup.verificationNameUnknown'),
        }),
      );
    } catch (e) {
      setError(isApiError(e) && e.code === 'VNNIC_REGISTRAR_API_DISABLED' ? t('taxLookup.notConnected') : isApiError(e) ? e.message : t('taxLookup.failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 grid gap-1.5">
      <div>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void run()}>
          {busy ? t('taxLookup.running') : t('taxLookup.verification')}
        </Button>
      </div>
      {done ? <p className="text-sm">{done}</p> : null}
      {error ? <p className="text-sm text-critical">{error}</p> : null}
    </div>
  );
}

/** `/subjects/:id` */
export function SubjectPage() {
  const { t } = useTranslation('domains');
  const label = useEnumLabel();
  const { t: ta } = useTranslation('accounts');
  const { t: td } = useTranslation('declarations');
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const tab = params.get('tab') === 'declarations' ? 'declarations' : 'overview';
  // `?edit=1` (links from a stopped registration's "what to fix") opens the edit form.
  const [editing, setEditing] = useState(params.get('edit') === '1');
  const result = useApi((signal) => api.getSubject(id, { signal }), [id]);
  const legalName = result.status === 'success' ? currentVersionOf(result.data)?.legal_name : undefined;
  const name = legalName ?? (result.status === 'success' ? t('subject.fallbackTitle') : t('subject.pageTitle'));
  useDocumentTitle(legalName ? t('subject.docTitle', { name: legalName }) : t('subject.pageTitle'));
  const crumbs = [{ label: t('subject.crumbBusiness') }, { label: t('subject.crumbSubjects'), to: '/subjects' }, { label: name }];
  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('subject.pageTitle')} crumbs={crumbs.slice(0, 2)} />
        <Loading label={t('subject.loading')} rows={4} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={t('subject.pageTitle')} crumbs={crumbs.slice(0, 2)} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('subject.notFound') : t('subject.loadFailed')} />
      </>
    );
  }
  return (
    <>
      <PageHeader
        title={name}
        subtitle={`${label('types', result.data.subject_type)} · ${t('subject.pageSubtitle')}`}
        crumbs={crumbs}
        actions={
          result.data.subject_type === 'INDIVIDUAL' || result.data.subject_type === 'ORGANIZATION' ? (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="size-4" aria-hidden="true" /> {ta('subjectForm.editButton')}
            </Button>
          ) : undefined
        }
      />
      <div className="mb-4">
        <LinkedWork subjectId={id} />
      </div>
      <SubjectFormDialog open={editing} onOpenChange={setEditing} subjectId={id} onSaved={() => result.reload()} />
      <NavTabs
        label={name}
        active={tab}
        tabs={[
          { id: 'overview', label: t('subject.pageTitle'), to: `/subjects/${encodeURIComponent(id)}` },
          { id: 'declarations', label: td('subject.tab'), to: `/subjects/${encodeURIComponent(id)}?tab=declarations` },
        ]}
      />
      {tab === 'declarations' ? (
        <SubjectDeclarations subjectId={id} />
      ) : (
        <SubjectBody s={result.data} extra={<SubjectDomainSync subjectId={id} />} />
      )}
    </>
  );
}
